import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { pctSymbol, currencySymbol } from '../../utils/formatters';

// Lightweight shadcn-like card wrappers
const Card = ({ className = '', children }) => (
  <div className={`rounded-md border border-succession-border bg-succession-bg-primary ${className}`}>{children}</div>
);
const CardHeader = ({ children }) => (
  <div className="px-4 pt-4">{children}</div>
);
const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-xl font-semibold ${className}`}>{children}</h3>
);
const CardContent = ({ children }) => (
  <div className="p-4">{children}</div>
);

// Resolve a CSS variable to a concrete color string from the active tenant theme
const resolveVar = (varName) => {
  try {
    if (typeof window === 'undefined' || !window.document) return `var(${varName})`;
    const val = getComputedStyle(document.documentElement).getPropertyValue(varName);
    return (val && val.trim()) ? val.trim() : `var(${varName})`;
  } catch (e) {
    return `var(${varName})`;
  }
};

// Category label mapping for display
const CATEGORY_LABELS = {
  admin_staff_compensation: 'Admin Staff Compensation',
  payroll_taxes: 'Payroll Taxes',
  cost_of_benefits: 'Cost of Benefits',
  training_education: 'Training & Education',
  occupancy_cost: 'Occupancy Cost',
  technology_cost: 'Technology Cost',
  professional_services: 'Professional Services',
  marketing_business_dev: 'Marketing & Business Dev',
  office_expenses: 'Office Expenses',
  travel: 'Travel',
  business_insurance: 'Business Insurance',
  depreciation_amortization: 'Depreciation/Amortization',
  other_expenses: 'Other Expenses',
};

// Map frontend expense category keys to benchmark keys
const FRONTEND_TO_BENCHMARK_KEY = {
  // Frontend key -> Benchmark key
  compensation: 'admin_staff_compensation',
  benefits: 'cost_of_benefits',
  occupancy: 'occupancy_cost',
  technology: 'technology_cost',
  professional: 'professional_services',
  marketing: 'marketing_business_dev',
  travel: 'travel',
  insurance: 'business_insurance',
  other: 'other_expenses',
  // Also support direct benchmark keys
  admin_staff_compensation: 'admin_staff_compensation',
  payroll_taxes: 'payroll_taxes',
  cost_of_benefits: 'cost_of_benefits',
  training_education: 'training_education',
  occupancy_cost: 'occupancy_cost',
  technology_cost: 'technology_cost',
  professional_services: 'professional_services',
  marketing_business_dev: 'marketing_business_dev',
  office_expenses: 'office_expenses',
  business_insurance: 'business_insurance',
  depreciation_amortization: 'depreciation_amortization',
  other_expenses: 'other_expenses',
};

// Map category labels (case-insensitive, partial match) to benchmark keys
// This handles tenant-customized categories with generated keys like "category_123456_0"
const LABEL_TO_BENCHMARK_KEY = {
  'admin': 'admin_staff_compensation',
  'compensation': 'admin_staff_compensation',
  'support staff': 'admin_staff_compensation',
  'payroll': 'payroll_taxes',
  'benefits': 'cost_of_benefits',
  'training': 'training_education',
  'education': 'training_education',
  'dues': 'training_education',
  'licensing': 'training_education',
  'occupancy': 'occupancy_cost',
  'rent': 'occupancy_cost',
  'utilities': 'occupancy_cost',
  'technology': 'technology_cost',
  'software': 'technology_cost',
  'hardware': 'technology_cost',
  'professional services': 'professional_services',
  'accounting': 'professional_services',
  'legal': 'professional_services',
  'compliance': 'professional_services',
  'consult': 'professional_services',
  'marketing': 'marketing_business_dev',
  'business development': 'marketing_business_dev',
  'office expenses': 'office_expenses',
  'office supplies': 'office_expenses',
  'supplies': 'office_expenses',
  'postage': 'office_expenses',
  'travel': 'travel',
  'insurance': 'business_insurance',
  'e&o': 'business_insurance',
  'depreciation': 'depreciation_amortization',
  'amortization': 'depreciation_amortization',
  'other': 'other_expenses',
  'miscellaneous': 'other_expenses',
  'taxes': 'other_expenses',
  'licenses': 'other_expenses',
};

// Find benchmark key from category key or label
const findBenchmarkKey = (key, label) => {
  // First try direct key mapping
  if (FRONTEND_TO_BENCHMARK_KEY[key]) {
    return FRONTEND_TO_BENCHMARK_KEY[key];
  }
  
  // Then try label-based matching (case-insensitive)
  if (label) {
    const labelLower = label.toLowerCase();
    for (const [pattern, benchmarkKey] of Object.entries(LABEL_TO_BENCHMARK_KEY)) {
      if (labelLower.includes(pattern)) {
        return benchmarkKey;
      }
    }
  }
  
  return null; // No mapping found
};

// Short labels for X-axis
const SHORT_LABELS = {
  admin_staff_compensation: 'Admin Staff',
  payroll_taxes: 'Payroll Tax',
  cost_of_benefits: 'Benefits',
  training_education: 'Training',
  occupancy_cost: 'Occupancy',
  technology_cost: 'Technology',
  professional_services: 'Prof. Svc',
  marketing_business_dev: 'Marketing',
  office_expenses: 'Office',
  travel: 'Travel',
  business_insurance: 'Insurance',
  depreciation_amortization: 'Deprec.',
  other_expenses: 'Other',
};

const formatCurrency = (value) => {
  const v = Number(value || 0);
  const sym = currencySymbol();
  if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${sym}${(v / 1_000).toFixed(0)}K`;
  return `${sym}${v.toFixed(0)}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  
  const categoryKey = payload[0]?.payload?.category;
  const fullLabel = CATEGORY_LABELS[categoryKey] || label;
  
  return (
    <div className="bg-succession-bg-primary border border-succession-border rounded-md p-3 shadow-lg">
      <p className="font-semibold text-succession-text-primary mb-2">{fullLabel}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 text-sm">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: entry.fill || entry.color }}
          />
          <span className="text-succession-text-secondary">{entry.name}:</span>
          <span className="font-medium text-succession-text-primary">{formatCurrency(entry.value)}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * OperatingExpensesBenchmarkChart - Compares user's operating expenses to peer benchmark
 * 
 * @param {Object} props
 * @param {Object|Array} props.userExpenses - User's operating expenses: either { key: amount } or [{ key, label, amount }]
 * @param {Object} props.benchmarkData - Benchmark income_statement_data from IndustryBenchmark
 * @param {number} props.branchNetRevenue - User's branch net revenue for context
 * @param {string} props.tierName - Current tier name (e.g., "$250-$500K")
 * @param {boolean} props.useBenchmarkMode - Whether user is using benchmark-calculated expenses
 * @param {string} props.className - Additional CSS classes
 */
export default function OperatingExpensesBenchmarkChart({
  userExpenses = {},
  benchmarkData = {},
  branchNetRevenue = 0,
  tierName = '',
  useBenchmarkMode = false,
  className = ''
}) {
  const primaryColor = resolveVar('--succession-primary');
  const secondaryColor = resolveVar('--succession-secondary');

  // Build chart data from user expenses and benchmark
  const chartData = useMemo(() => {
    const benchmarkCategories = Object.keys(CATEGORY_LABELS);
    
    // Map user expenses to benchmark keys
    // Supports both formats:
    // 1. Array of category objects: [{ key, label, amount }]
    // 2. Object with key-value pairs: { key: amount }
    const mappedUserExpenses = {};
    
    if (Array.isArray(userExpenses)) {
      // Array format from operatingExpenseCategories
      userExpenses.forEach((cat) => {
        const amount = Number(cat?.amount || 0);
        if (amount <= 0) return;
        
        const benchmarkKey = findBenchmarkKey(cat?.key, cat?.label);
        if (benchmarkKey) {
          mappedUserExpenses[benchmarkKey] = (mappedUserExpenses[benchmarkKey] || 0) + amount;
        } else {
          // Fallback to "other_expenses" if no mapping found
          mappedUserExpenses['other_expenses'] = (mappedUserExpenses['other_expenses'] || 0) + amount;
        }
      });
    } else {
      // Object format: { key: amount }
      Object.entries(userExpenses).forEach(([key, value]) => {
        const amount = Number(value || 0);
        if (amount <= 0) return;
        
        const benchmarkKey = findBenchmarkKey(key, null);
        if (benchmarkKey) {
          mappedUserExpenses[benchmarkKey] = (mappedUserExpenses[benchmarkKey] || 0) + amount;
        } else {
          // Fallback to "other_expenses" if no mapping found
          mappedUserExpenses['other_expenses'] = (mappedUserExpenses['other_expenses'] || 0) + amount;
        }
      });
    }
    
    return benchmarkCategories.map((category) => {
      const userValue = Number(mappedUserExpenses[category] || 0);
      
      // Get benchmark value from income_statement_data
      const benchmarkEntry = benchmarkData[category];
      const benchmarkValue = benchmarkEntry?.amount != null 
        ? Number(benchmarkEntry.amount) 
        : 0;
      
      return {
        category,
        label: SHORT_LABELS[category] || category,
        fullLabel: CATEGORY_LABELS[category] || category,
        user: userValue,
        benchmark: benchmarkValue,
      };
    }).filter(item => item.user > 0 || item.benchmark > 0); // Only show categories with values
  }, [userExpenses, benchmarkData]);

  // Calculate totals
  const totals = useMemo(() => ({
    user: chartData.reduce((sum, item) => sum + item.user, 0),
    benchmark: chartData.reduce((sum, item) => sum + item.benchmark, 0),
  }), [chartData]);

  // Check if user has meaningful expense data
  const hasUserData = totals.user > 0;
  const onlyHasOther = hasUserData && chartData.length === 1 && chartData[0]?.category === 'other_expenses';

  if (chartData.length === 0) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>Operating Expenses vs. Peer Benchmark</CardTitle>
        {tierName && (
          <p className="text-sm text-succession-text-secondary mt-1">
            Comparing to practices in the {tierName} revenue tier
          </p>
        )}
        {useBenchmarkMode && (
          <p className="text-xs text-amber-600 mt-2 bg-amber-50 px-3 py-2 rounded-md">
            Note: You are using benchmark-calculated operating expenses. To see a detailed category comparison, 
            turn off the benchmark toggle in the Expenses tab and enter your actual operating expenses by category.
          </p>
        )}
        {!useBenchmarkMode && onlyHasOther && (
          <p className="text-xs text-blue-600 mt-2 bg-blue-50 px-3 py-2 rounded-md">
            Note: Your operating expenses are shown as a total. For a more detailed comparison, 
            enter your expenses by category in the Expenses tab.
          </p>
        )}
      </CardHeader>
      <CardContent>
        <div className="mb-4">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--succession-border)" />
              <XAxis
                dataKey="label"
                tick={{ fill: 'var(--succession-text-secondary)', fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis
                tick={{ fill: 'var(--succession-text-secondary)', fontSize: 12 }}
                tickFormatter={formatCurrency}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                wrapperStyle={{ paddingBottom: 10 }}
              />
              <Bar
                name="Your Practice"
                dataKey="user"
                fill={primaryColor}
                radius={[4, 4, 0, 0]}
              />
              <Bar
                name="Peer Benchmark"
                dataKey="benchmark"
                fill={secondaryColor}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Summary comparison */}
        <div className="mt-6 pt-4 border-t border-succession-border">
          <div className="grid grid-cols-2 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="p-4 rounded-lg bg-succession-bg-tertiary"
            >
              <div className="text-sm text-succession-text-secondary mb-1">Your Total Operating Expenses</div>
              <div className="text-xl font-bold text-succession-text-primary">{formatCurrency(totals.user)}</div>
              {branchNetRevenue > 0 && (
                <div className="text-xs text-succession-text-secondary mt-1">
                  {((totals.user / branchNetRevenue) * 100).toFixed(1)}{pctSymbol()} of revenue
                </div>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="p-4 rounded-lg bg-succession-bg-tertiary"
            >
              <div className="text-sm text-succession-text-secondary mb-1">Peer Benchmark Total</div>
              <div className="text-xl font-bold text-succession-text-primary">{formatCurrency(totals.benchmark)}</div>
              {branchNetRevenue > 0 && totals.benchmark > 0 && (
                <div className="text-xs text-succession-text-secondary mt-1">
                  {((totals.benchmark / (benchmarkData.branch_net_revenue?.amount || branchNetRevenue)) * 100).toFixed(1)}{pctSymbol()} of benchmark revenue
                </div>
              )}
            </motion.div>
          </div>

          {/* Variance indicator */}
          {totals.user > 0 && totals.benchmark > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              className="mt-4 text-center"
            >
              {totals.user > totals.benchmark ? (
                <span className="text-sm">
                  Your expenses are{' '}
                  <span className="font-semibold text-red-600">
                    {formatCurrency(totals.user - totals.benchmark)} higher
                  </span>
                  {' '}than the peer benchmark
                </span>
              ) : totals.user < totals.benchmark ? (
                <span className="text-sm">
                  Your expenses are{' '}
                  <span className="font-semibold text-green-600">
                    {formatCurrency(totals.benchmark - totals.user)} lower
                  </span>
                  {' '}than the peer benchmark
                </span>
              ) : (
                <span className="text-sm text-succession-text-secondary">
                  Your expenses match the peer benchmark
                </span>
              )}
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
